import { useState, useMemo } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import Logo from "@/components/Logo";
import { ArrowRight, Box, Building2, Briefcase, CheckCircle2, Circle } from "lucide-react";
import PhonePrefixInput from "@/components/PhonePrefixInput";
import { passwordChecks, validatePasswordPolicy } from "@/lib/password_policy";
import { DOC_COUNTRIES, platePlaceholder, licensePlaceholder } from "@/lib/doc_country";
import { registrationIntent, registrationLink, registrationReturnPath } from "@/lib/registrationIntent";
import { loginDestination } from "@/lib/loginDestination";
import { normalizedCustomerAddress, validateCustomerAddress } from "@/lib/customerAddress";

const ROLE_OPTIONS = [
  { id: "customer_private", label: "Privato", icon: Box, desc: "Per spedizioni occasionali" },
  { id: "customer_business", label: "Azienda", icon: Building2, desc: "Tariffe e fatture aziendali" },
  { id: "customer_shop", label: "Negozio", icon: Briefcase, desc: "Consegne ai tuoi clienti" },
];

export default function Register() {
  const [params] = useSearchParams();
  const intent = registrationIntent(params);
  if (intent.driverRequested) return <Navigate to="/become-driver" replace />;
  // Query changes on the same route must not retain a role from the other audience.
  return <RegistrationForm key={`${intent.audience}:${intent.role}`} params={params} intent={intent} />;
}

function RegistrationForm({ params, intent }) {
  const { register } = useAuth();
  const navigate = useNavigate();
  // Phase 41 — Plan preselected from B2B landing CTAs (?plan=start|pro)
  const planParam = intent.plan;
  const targetSubPlan = planParam === "start" ? "business_start" : planParam === "pro" ? "business_pro" : null;
  // Phase 44.06 — Recipient referral (from /track/{token})
  const referralToken = params.get("ref") || "";
  const referralSource = params.get("src") || (referralToken ? "public_tracking" : "");
  const initialRole = intent.role;
  const initialStep = intent.audience === "private" ? 2 : 1;

  const [step, setStep] = useState(initialStep);
  const [role, setRole] = useState(initialRole);
  const [form, setForm] = useState({
    email: "",
    password: "",
    password_confirm: "",
    name: "",
    phone: "",
    phone_prefix: "+41",
    business_name: "",
    vehicle_type: "",
    vehicle_plate: "",
    license_number: "",
    doc_country: "CH",
    referral_code: "",
  });
  const [taxAgreement, setTaxAgreement] = useState(false);
  const [address, setAddress] = useState({ street: "", house_number: "", zip: "", city: "", country: "Svizzera" });
  // Phase 44.31.15 — dichiarazione anti-duplicato obbligatoria driver
  const [uniqueProfileAgreement, setUniqueProfileAgreement] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const pwChecks = useMemo(() => passwordChecks(form.password), [form.password]);
  const pwMismatch = useMemo(
    () => form.password_confirm.length > 0 && form.password !== form.password_confirm,
    [form.password, form.password_confirm]
  );

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const isBusiness = role === "customer_business" || role === "customer_shop";
  const isDriver = role === "driver";

  const onSubmit = async (e) => {
    e.preventDefault();
    if (intent.audience === "private" ? role !== "customer_private" : !["customer_business", "customer_shop"].includes(role)) {
      setError("Seleziona un tipo di account valido.");
      return;
    }
    if (isDriver && !taxAgreement) {
      setError("Devi accettare la dichiarazione di responsabilità fiscale per registrarti come trasportatore.");
      toast.error("Spunta la dichiarazione fiscale per continuare.");
      return;
    }
    if (isDriver && !uniqueProfileAgreement) {
      setError("Devi accettare la dichiarazione di profilo unico per registrarti come trasportatore.");
      toast.error("Spunta la dichiarazione anti-duplicato per continuare.");
      return;
    }
    // Phase 32 Batch A — password policy enforcement client-side
    const policyErr = validatePasswordPolicy(form.password);
    if (policyErr) {
      setError(policyErr);
      toast.error(policyErr);
      return;
    }
    // Phase 37 — conferma password (deve coincidere)
    if (form.password !== form.password_confirm) {
      const msg = "Le password non coincidono.";
      setError(msg);
      toast.error(msg);
      return;
    }
    if (!(form.phone || "").trim()) {
      setError("Il numero di telefono è obbligatorio.");
      toast.error("Inserisci un numero di telefono.");
      return;
    }
    const addressError = validateCustomerAddress(address);
    if (addressError) {
      setError(addressError);
      toast.error(addressError);
      return;
    }
    setSubmitting(true);
    setError("");
    const payload = {
      email: form.email,
      password: form.password,
      name: form.name,
      phone: form.phone,
      phone_prefix: form.phone_prefix || "+41",
      role,
      business_name: isBusiness ? form.business_name : undefined,
      address: normalizedCustomerAddress(address),
      vehicle_type: isDriver ? form.vehicle_type : undefined,
      vehicle_plate: isDriver ? form.vehicle_plate : undefined,
      license_number: isDriver ? form.license_number : undefined,
      referral_code: isDriver && form.referral_code ? form.referral_code.trim().toUpperCase() : undefined,
      tax_agreement_accepted: isDriver ? true : undefined,
      unique_profile_agreement_accepted: isDriver ? true : undefined,
      // Phase 44.06 — recipient referral context
      recipient_referral_token: !isDriver && referralToken ? referralToken : undefined,
      recipient_referral_source: !isDriver && referralToken ? (referralSource || "public_tracking") : undefined,
    };
    const res = await register(payload);
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error);
      toast.error(res.error);
      return;
    }
    toast.success("Account creato!");
    // Phase 44.06 + 44.11 — Track referral signup completed (authed user)
    // Doppio evento per distinguere il referral premium post-delivered da
    // quello generico durante il tracking attivo.
    if (!isDriver && referralToken) {
      try {
        const { trackEvent } = await import("@/lib/analytics");
        trackEvent("referral_signup_completed", {
          source: referralSource || "public_tracking",
          ref_token_prefix: referralToken.slice(0, 8),
        });
        if (referralSource === "delivered_tracking") {
          trackEvent("delivered_referral_signup", {
            ref_token_prefix: referralToken.slice(0, 8),
          });
        }
      } catch { /* silent */ }
    }
    // Registration never starts a payment: keep the chosen plan for review.
    if (isBusiness && targetSubPlan) {
      navigate(`/app/subscription?plan=${targetSubPlan}`, { replace: true });
      return;
    }
    navigate(loginDestination(res.user.role, [params.get("next"), registrationReturnPath(params, res.user.role)], window.location.origin), { replace: true });
  };

  return (
    <div className="min-h-screen bg-dapnow-bg">
      <header className="px-4 sm:px-6 pt-4 sm:pt-6 max-w-6xl mx-auto pt-[env(safe-area-inset-top)]">
        <div className="flex items-center justify-between gap-4 min-h-24">
          <Logo size="lg" />
          <Link to={registrationLink(params, { role, to: "/login" })} data-testid="register-login-link" className="text-sm font-semibold text-dapnow-blue hover:underline">Accedi</Link>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-4 sm:px-6 py-8">
        <p className="overline">Crea account</p>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter mt-1">
          Inizia con DapNow
        </h1>
        <p className="text-dapnow-muted mt-2" data-testid="register-audience-description">{intent.audience === "private" ? "Crea il tuo account privato per organizzare le tue consegne." : "Crea un account per la tua azienda o il tuo negozio."}</p>
        <nav aria-label="Tipo di cliente" className="mt-5 inline-flex rounded-full border border-dapnow-line bg-white p-1 gap-1">
          {[["private", "Privati"], ["business", "Aziende"]].map(([audience, label]) => (
            <Link key={audience} to={registrationLink(params, { audience })} data-testid={`register-audience-${audience}`}
              aria-current={intent.audience === audience ? "page" : undefined}
              className={`inline-flex h-10 items-center px-5 rounded-full text-sm font-semibold ${intent.audience === audience ? "bg-dapnow-blue text-white" : "text-dapnow-ink"}`}>
              {label}
            </Link>
          ))}
        </nav>

        {referralToken && (
          <div
            className="mt-5 rounded-2xl border-2 border-emerald-300 bg-emerald-50/80 p-4 flex items-start gap-3"
            data-testid="register-referral-badge"
          >
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-600 text-white shrink-0">
              <CheckCircle2 className="h-5 w-5" />
            </span>
            <div>
              <p className="font-display text-base font-bold tracking-tight text-emerald-900">
                CHF 5 di credito al primo ordine
              </p>
              <p className="text-xs text-emerald-800 mt-0.5 leading-snug">
                Stai creando l'account dal link tracking di un amico. Completa la registrazione: il credito verrà accreditato automaticamente al tuo primo ordine completato.
              </p>
            </div>
          </div>
        )}

        {intent.audience === "business" && <div className="mt-8 grid grid-cols-2 gap-2 max-w-sm">
          <button
            type="button"
            onClick={() => setStep(1)}
            className={`h-2 rounded-full ${step === 1 ? "bg-dapnow-blue" : "bg-dapnow-line"}`}
            aria-label="Step 1"
          />
          <button
            type="button"
            onClick={() => form.email && setStep(2)}
            className={`h-2 rounded-full ${step === 2 ? "bg-dapnow-blue" : "bg-dapnow-line"}`}
            aria-label="Step 2"
          />
        </div>}

        {step === 1 && (
          <div className="mt-8">
            <p className="overline">Scegli il tipo di account</p>
            <div className="mt-3 grid sm:grid-cols-2 gap-3">
              {ROLE_OPTIONS.filter(r => r.id !== "customer_private").map((r) => (
                <button
                  key={r.id}
                  type="button"
                  data-testid={`role-option-${r.id}`}
                  aria-pressed={role === r.id}
                  onClick={() => setRole(r.id)}
                  className={`text-left p-5 rounded-2xl border transition ${
                    role === r.id
                      ? "border-dapnow-blue bg-dapnow-blue-soft shadow-sm"
                      : "border-dapnow-line bg-white hover:border-dapnow-blue/40"
                  }`}
                >
                  <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${role === r.id ? "bg-dapnow-blue text-white" : "bg-dapnow-bg text-dapnow-ink"}`}>
                    <r.icon className="h-5 w-5" />
                  </span>
                  <p className="mt-3 font-display text-lg font-semibold tracking-tight">{r.label}</p>
                  <p className="text-xs text-dapnow-muted">{r.desc}</p>
                </button>
              ))}
            </div>
            {/* Phase 43.39 — Driver dirottato alla sua pagina dedicata, niente confusione lato cliente */}
            <p className="mt-5 text-xs text-dapnow-muted" data-testid="register-driver-hint">
              Ti interessa consegnare come driver privato?{" "}
              <Link to="/become-driver" className="font-semibold text-dapnow-blue hover:underline">
                Servizio in arrivo: richiedi informazioni →
              </Link>
            </p>
            <button
              onClick={() => setStep(2)}
              data-testid="register-step1-next"
              className="btn-primary mt-8"
            >
              Continua <ArrowRight className="ml-1.5 h-4 w-4" />
            </button>
          </div>
        )}

        {step === 2 && (
          <form className="mt-8 space-y-4" onSubmit={onSubmit}>
            <p className="text-sm font-semibold" data-testid="register-selected-account">Account {ROLE_OPTIONS.find(option => option.id === role)?.label.toLowerCase()}</p>
            {/* Phase 41 — Banner piano preselected (Business Start/Pro dal CTA B2B landing) */}
            {isBusiness && targetSubPlan && (
              <div
                className="rounded-2xl border border-dapnow-blue/30 bg-dapnow-blue-soft/50 p-4 flex items-start gap-3"
                data-testid="register-plan-banner"
              >
                <span className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-white text-dapnow-blue ring-1 ring-dapnow-blue/20">
                  <Building2 className="h-5 w-5" />
                </span>
                <div className="flex-1">
                  <p className="overline text-dapnow-blue">Piano selezionato</p>
                  <p className="font-display text-lg font-bold tracking-tight" data-testid="register-plan-name">
                    {targetSubPlan === "business_start" ? "Business Start" : "Business Pro"}
                  </p>
                  <p className="text-xs text-dapnow-muted mt-0.5">
                    Dopo la registrazione puoi consultare i dettagli del piano. Creare l’account non attiva un abbonamento e non avvia un pagamento.
                  </p>
                </div>
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-semibold tracking-tight">Nome completo</label>
                <input data-testid="register-name-input" required value={form.name} onChange={set("name")} className="input-tall mt-1.5 w-full" placeholder="Mario Rossi" />
              </div>
              <PhonePrefixInput
                label="Telefono"
                testidPrefix="register-phone"
                prefix={form.phone_prefix}
                onPrefixChange={(p) => setForm((s) => ({ ...s, phone_prefix: p }))}
                number={form.phone}
                onNumberChange={(n) => setForm((s) => ({ ...s, phone: n }))}
                required
                placeholder="76 123 45 67"
              />
            </div>

            <div>
              <label className="text-sm font-semibold tracking-tight">Email</label>
              <input type="email" required data-testid="register-email-input" value={form.email} onChange={set("email")} className="input-tall mt-1.5 w-full" placeholder="tu@email.ch" />
            </div>
            <div>
              <label className="text-sm font-semibold tracking-tight">Password</label>
              <input type="password" required minLength={8} data-testid="register-password-input" value={form.password} onChange={set("password")} className="input-tall mt-1.5 w-full" placeholder="••••••••" />
              <ul className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1" data-testid="register-password-policy">
                {[
                  { ok: pwChecks.length, label: "8 caratteri" },
                  { ok: pwChecks.letter, label: "una lettera" },
                  { ok: pwChecks.digit, label: "un numero" },
                  { ok: pwChecks.symbol, label: "un simbolo" },
                ].map((c) => {
                  const I = c.ok ? CheckCircle2 : Circle;
                  return (
                    <li key={c.label} className={`flex items-center gap-1.5 text-[11px] ${c.ok ? "text-emerald-700" : "text-dapnow-muted"}`}>
                      <I className="h-3.5 w-3.5" /> {c.label}
                    </li>
                  );
                })}
              </ul>
            </div>
            <div>
              <label className="text-sm font-semibold tracking-tight">Conferma password</label>
              <input
                type="password"
                required
                minLength={8}
                data-testid="register-password-confirm-input"
                value={form.password_confirm}
                onChange={set("password_confirm")}
                className={`input-tall mt-1.5 w-full ${pwMismatch ? "border-dapnow-red focus:border-dapnow-red" : ""}`}
                placeholder="••••••••"
                aria-invalid={pwMismatch}
              />
              {pwMismatch && (
                <p className="mt-1 text-xs text-dapnow-red font-semibold" data-testid="register-password-mismatch">
                  Le password non coincidono.
                </p>
              )}
            </div>

            {isBusiness && (
              <div>
                <label className="text-sm font-semibold tracking-tight">Ragione sociale</label>
                <input data-testid="register-business-input" value={form.business_name} onChange={set("business_name")} className="input-tall mt-1.5 w-full" placeholder="Ragione sociale della tua azienda" />
              </div>
            )}

            <fieldset className="rounded-2xl border border-dapnow-line bg-white p-4 sm:p-5" data-testid="register-address-fields">
              <legend className="px-1 text-sm font-semibold">{isBusiness ? "Indirizzo della sede" : "Indirizzo del tuo account"}</legend>
              <p className="text-xs text-dapnow-muted mb-4">Gli indirizzi di ritiro e consegna si scelgono separatamente per ogni spedizione. Le consegne restano disponibili solo nell’area servita.</p>
              <div className="grid sm:grid-cols-4 gap-4">
                {[
                  { key: "street", label: "Via", placeholder: "Via Nassa", maxLength: 200, autocomplete: "address-line1", span: "sm:col-span-3" },
                  { key: "house_number", label: "Numero civico", placeholder: "10", maxLength: 20, autocomplete: "off", span: "sm:col-span-1" },
                  { key: "zip", label: "CAP", placeholder: "6900", maxLength: 20, autocomplete: "postal-code", span: "sm:col-span-1" },
                  { key: "city", label: "Località", placeholder: "Lugano", maxLength: 100, autocomplete: "address-level2", span: "sm:col-span-3" },
                  { key: "country", label: "Paese", placeholder: "Svizzera", maxLength: 80, autocomplete: "country-name", span: "sm:col-span-4" },
                ].map(field => (
                  <div key={field.key} className={field.span}>
                    <label htmlFor={`register-address-${field.key}`} className="text-sm font-semibold tracking-tight">{field.label}</label>
                    <input id={`register-address-${field.key}`} data-testid={`register-address-${field.key}`} required
                      value={address[field.key]} onChange={e => setAddress(current => ({ ...current, [field.key]: e.target.value }))}
                      maxLength={field.maxLength} autoComplete={field.autocomplete}
                      className="input-tall mt-1.5 w-full" placeholder={field.placeholder} />
                  </div>
                ))}
              </div>
            </fieldset>

            {isDriver && (
              <>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-semibold tracking-tight">Tipo veicolo</label>
                    <select data-testid="register-vehicle-type" value={form.vehicle_type} onChange={set("vehicle_type")} className="input-tall mt-1.5 w-full">
                      <option value="">Seleziona…</option>
                      <option value="moto">Moto</option>
                      <option value="scooter">Scooter</option>
                      <option value="auto">Auto</option>
                      <option value="auto_grande">Auto grande / SUV</option>
                      <option value="furgone">Furgone</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-semibold tracking-tight">Paese targa/patente</label>
                    <select
                      data-testid="register-doc-country"
                      value={form.doc_country}
                      onChange={set("doc_country")}
                      className="input-tall mt-1.5 w-full"
                    >
                      {DOC_COUNTRIES.map((c) => (
                        <option key={c.id} value={c.id}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-semibold tracking-tight">Targa</label>
                    <input
                      data-testid="register-vehicle-plate"
                      value={form.vehicle_plate}
                      onChange={set("vehicle_plate")}
                      className="input-tall mt-1.5 w-full"
                      placeholder={platePlaceholder(form.doc_country)}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold tracking-tight">Numero patente</label>
                    <input
                      data-testid="register-license-number"
                      value={form.license_number}
                      onChange={set("license_number")}
                      className="input-tall mt-1.5 w-full"
                      placeholder={licensePlaceholder(form.doc_country)}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-sm font-semibold tracking-tight">Codice referral (opz.)</label>
                    <input data-testid="register-referral-code" value={form.referral_code || ""} onChange={set("referral_code")} className="input-tall mt-1.5 w-full uppercase" placeholder="DAPXXXXX" />
                    <p className="text-xs text-dapnow-muted mt-1">Hai un codice da un altro driver? Inseriscilo qui per attivare il bonus referral.</p>
                  </div>
                </div>
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-4 mt-2" data-testid="register-commuter-note">
                  <p className="text-xs leading-relaxed text-emerald-900">
                    <strong className="font-semibold">Commuter Delivery Network.</strong>{" "}
                    Subito dopo la registrazione potrai indicare i tuoi <strong>tragitti pendolari abituali</strong>
                    (casa↔lavoro, scuola, palestra…) in Ticino o Grigioni italiano e la deviazione massima accettata.
                    Ti proporremo solo ordini Shared sul tuo percorso — senza obbligo, modificabile in qualunque momento.
                  </p>
                </div>
                <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4 mt-2">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="register-tax-agreement"
                      checked={taxAgreement}
                      onChange={(e) => setTaxAgreement(e.target.checked)}
                      className="mt-1 h-5 w-5 rounded border-dapnow-line text-dapnow-blue focus:ring-dapnow-blue accent-dapnow-blue shrink-0"
                    />
                    <span className="text-xs leading-relaxed text-dapnow-ink">
                      <strong className="font-semibold">Dichiarazione di responsabilità fiscale (obbligatoria).</strong>{" "}
                      Confermo di essere responsabile della corretta dichiarazione fiscale e previdenziale dei compensi
                      ricevuti tramite DapNow e di verificare la mia posizione con AVS / autorità fiscali se necessario.
                      DapNow fornisce un riepilogo dei compensi ma non offre consulenza fiscale.
                    </span>
                  </label>
                </div>
                {/* Phase 44.31.15 — Dichiarazione profilo unico (anti-duplicato) */}
                <div className="rounded-2xl border border-red-200 bg-red-50/60 p-4 mt-2">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="register-unique-profile-agreement"
                      checked={uniqueProfileAgreement}
                      onChange={(e) => setUniqueProfileAgreement(e.target.checked)}
                      className="mt-1 h-5 w-5 rounded border-dapnow-line text-dapnow-blue focus:ring-dapnow-blue accent-dapnow-blue shrink-0"
                    />
                    <span className="text-xs leading-relaxed text-dapnow-ink">
                      <strong className="font-semibold">Dichiarazione di profilo unico (obbligatoria).</strong>{" "}
                      Ogni persona fisica può avere un solo profilo driver DapNow. È vietato creare profili
                      multipli, usare dati di terzi o intestare account a prestanome per aggirare obblighi fiscali,
                      contributivi, assicurativi o di verifica. La violazione comporta sospensione immediata
                      dell'account, blocco dei payout e segnalazione alle autorità competenti.
                    </span>
                  </label>
                </div>
              </>
            )}

            {error && <p data-testid="register-error" className="text-sm text-dapnow-red">{error}</p>}
            <div className="flex gap-3 pt-2">
              {intent.audience === "business" && <button type="button" onClick={() => setStep(1)} className="btn-ghost">Indietro</button>}
              <button type="submit" disabled={submitting} data-testid="register-submit-btn" className="btn-primary flex-1">
                {submitting ? "Creazione…" : "Crea account"}
              </button>
            </div>
            <p className="text-xs text-dapnow-muted">
              DapNow è un servizio di SwissNat SA. <Link to="/legal" className="underline">Informazioni sul gestore e contatti</Link>.
            </p>
          </form>
        )}
      </main>
    </div>
  );
}
